import {assertUnreachable, isNotNullOrUndefined} from '_/utilities/types';

import {
  Action,
  ActionType,
  appendToSelection,
  Item,
  Navigation,
  NavigationMode,
  setActiveItem,
  setSelection,
} from './actions';

export interface State<TItemType extends Item<TItemType> = Item<object>> {
  items: TItemType[];
  activeItem?: TItemType;
  selection: TItemType[];
  selectable: boolean;
  multiselectable: boolean;
  multiselecting: boolean;
  wrapped: boolean;
  topTouched?: boolean;
  bottomTouched?: boolean;
}

function clearEventsFromState<TItemType extends Item<TItemType> = Item<object>>(
  state: State<TItemType>
): State<TItemType> {
  return {...state, bottomTouched: undefined, topTouched: undefined};
}

export function createReducer<
  TItemType extends Item<TItemType> = Item<object>,
>(): (state: State<TItemType>, actions: Action<TItemType>) => State<TItemType> {
  // eslint-disable-next-line max-statements,complexity
  return function reducer(
    state_: State<TItemType>,
    action: Action<TItemType>
  ): State<TItemType> {
    const state = clearEventsFromState(state_);

    switch (action.type) {
      case ActionType.SetItems:
        return {
          ...state,
          activeItem: state.activeItem
            ? action.payload.newItems.includes(state.activeItem)
              ? state.activeItem
              : action.payload.newItems.find(
                  ({id}) => id != null && id === state.activeItem?.id
                )
            : undefined,
          items: action.payload.newItems,
          selection: state.selection
            .map((item) =>
              action.payload.newItems.includes(item)
                ? item
                : action.payload.newItems.find(
                    ({id, section}) =>
                      id != null && id === item.id && section === item.section
                  )
            )
            .filter(isNotNullOrUndefined),
        };

      case ActionType.SetActiveItem: {
        const activeItem = action.payload.newActiveItem;

        if (activeItem) {
          return {
            ...state,
            activeItem,
            multiselecting: false,
            selection: [activeItem],
          };
        }

        return {
          ...state,
          activeItem,
          multiselecting: false,
        };
      }
      case ActionType.SetSelection: {
        const selectedItemIds = action.payload.newSelection
          .filter((item) => item !== undefined)
          .map(
            (item) =>
              `${item.id}-${
                typeof item.section === 'object'
                  ? item.section.id
                  : item.section
              }`
          );
        return {
          ...state,
          ...(action.payload.newActiveItem
            ? {activeItem: action.payload.newActiveItem}
            : {}),
          ...(state.multiselectable && action.payload.newSelection.length > 1
            ? {multiselecting: true}
            : {}),
          ...(state.multiselectable && action.payload.newSelection.length === 0
            ? {activeItem: action.payload.newActiveItem, multiselecting: false}
            : {}),
          selection: state.items.filter((item) =>
            item
              ? item.id
                ? selectedItemIds.includes(
                    `${item.id}-${
                      typeof item.section === 'object'
                        ? item.section.id
                        : item.section
                    }`
                  )
                : action.payload.newSelection.includes(item)
              : false
          ),
        };
      }
      case ActionType.AppendToSelection:
        return {
          ...state,
          multiselecting: true,
          selection: state.items.filter(
            (item) =>
              !item.notSelectable &&
              (state.selection.includes(item) ||
                action.payload.appendingSelection.includes(item))
          ),
        };

      case ActionType.ToggleSelection:
        if (state.selection.includes(action.payload.togglingItem)) {
          return reducer(
            state,
            setSelection({
              newActiveItem: undefined,
              newSelection: state.selection.filter(
                (item) => item !== action.payload.togglingItem
              ),
            })
          );
        } else {
          return reducer(
            state,
            appendToSelection([action.payload.togglingItem])
          );
        }

      case ActionType.SelectDefault:
        return state;

      case ActionType.Navigate: {
        let nextIndex: number;

        switch (action.payload.navigation) {
          case Navigation.Up:
            nextIndex = state.items.indexOf(
              state.multiselectable || state.selectable
                ? state.selection[0]!
                : state.activeItem!
            );

            do {
              nextIndex -= 1;
              if (nextIndex < 0) {
                if (state.wrapped) {
                  nextIndex = state.items.length - 1;
                } else {
                  break;
                }
              }
            } while (state.items[nextIndex]!.inactive);

            if (nextIndex < 0) {
              if (
                action.payload.mode === NavigationMode.Normal &&
                state.multiselecting &&
                state.selection.length > 1
              ) {
                return {
                  ...state,
                  activeItem: state.selection[0],
                  multiselecting: false,
                  selection: [state.selection[0]!],
                };
              } else {
                return {
                  ...state,
                  topTouched: true,
                };
              }
            }
            break;

          case Navigation.Down:
            nextIndex = state.items.indexOf(
              state.multiselectable || state.selectable
                ? state.selection[state.selection.length - 1]!
                : state.activeItem!
            );

            do {
              nextIndex += 1;
              if (nextIndex >= state.items.length) {
                if (state.wrapped) {
                  nextIndex = 0;
                } else {
                  break;
                }
              }
            } while (state.items[nextIndex]?.inactive);

            if (nextIndex === state.items.length) {
              if (
                action.payload.mode === NavigationMode.Normal &&
                state.multiselecting &&
                state.selection.length > 1
              ) {
                return {
                  ...state,
                  activeItem: state.selection[state.selection.length - 1],
                  multiselecting: false,
                  selection: [state.selection[state.selection.length - 1]!],
                };
              } else {
                return {
                  ...state,
                  bottomTouched: true,
                };
              }
            }
            break;

          case Navigation.Home:
            nextIndex = 0;
            break;

          case Navigation.End:
            nextIndex = state.items.length - 1;
            break;

          case Navigation.Item:
            nextIndex = state.items.indexOf(action.payload.item);
            break;

          default:
            assertUnreachable(action.payload);
        }

        if (state.multiselectable) {
          if (action.payload.mode === NavigationMode.Selecting) {
            const firstEnd = state.items.indexOf(state.items[nextIndex]!);
            const secondEnd = state.items.indexOf(
              state.activeItem || state.items[0]!
            );

            return reducer(
              state,
              appendToSelection(
                state.items.filter(
                  (_, index) =>
                    index >= Math.min(firstEnd, secondEnd) &&
                    index <= Math.max(firstEnd, secondEnd)
                )
              )
            );
          } else {
            return reducer(state, setActiveItem(state.items[nextIndex]));
          }
        } else if (state.selectable) {
          return reducer(
            state,
            setSelection({
              newActiveItem: state.items[nextIndex],
              newSelection: [state.items[nextIndex]!],
            })
          );
        } else {
          return {
            ...state,
            activeItem: state.items[nextIndex],
          };
        }
      }
      case ActionType.SetMultiselecting:
        return {
          ...state,
          multiselecting: action.payload.multiselecting,
        };
    }
  };
}
